import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import PaintDeliveryStatus from "@/components/PaintDeliveryStatus";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, FileDown, FileText, Pencil, Trash2, Loader2, PaintBucket } from "lucide-react";
import { toast } from "sonner";

const shift = (m, d) => {
  const [y, mo] = m.split("-").map(Number);
  const dt = new Date(y, mo - 1 + d, 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
};
const label = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-AU", { month: "long", year: "numeric" });

function downloadStatement(ownerId, month, fmt, shopName) {
  return api.get(`/paint/statement?month=${month}&fmt=${fmt}&owner_id=${ownerId}`, { responseType: "blob" }).then((r) => {
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `paint-${shopName.replace(/\s+/g, "-").toLowerCase()}-${month}.${fmt}`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  }).catch(() => toast.error("Download failed"));
}

export default function PaintTab() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState("");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get(`/paint/admin/orders${month ? `?month=${month}` : ""}`)
      .then((r) => { setData(r.data); if (!month) setMonth(r.data.month); })
      .catch((e) => toast.error(formatApiErrorDetail(e, "Couldn't load paint orders")));
  }, [month]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/paint/config").then((r) => setItems(r.data.items)).catch(() => {}); }, []);

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-kilted-green" /></div>;
  const atCurrent = data.month >= data.current_month;

  const remove = async (oid) => {
    if (!window.confirm("Delete this paint order? This can't be undone.")) return;
    try {
      await api.delete(`/paint/admin/orders/${oid}`);
      toast.success("Paint order deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e, "Delete failed")); }
  };

  return (
    <div className="mt-4 space-y-4" data-testid="admin-paint-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setMonth(shift(data.month, -1))} data-testid="admin-paint-prev-month" className="gap-1"><ChevronLeft className="h-4 w-4" /> Prev</Button>
          <span className="min-w-[150px] text-center font-heading text-base font-extrabold text-kilted-green" data-testid="admin-paint-month">{label(data.month)}</span>
          <Button variant="outline" size="sm" disabled={atCurrent} onClick={() => setMonth(shift(data.month, 1))} data-testid="admin-paint-next-month" className="gap-1">Next <ChevronRight className="h-4 w-4" /></Button>
        </div>
        <div className="rounded-lg border border-kilted-gold/50 bg-kilted-gold/10 px-3 py-1.5 text-sm" data-testid="admin-paint-tally">
          <b>{data.tally.count}</b> orders · <b>${data.tally.total.toFixed(2)}</b> inc GST
        </div>
      </div>

      {data.shops.length === 0 && (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="admin-paint-empty">No paint shop accounts yet.</CardContent></Card>
      )}

      {data.shops.map((shop) => (
        <Card key={shop.owner_id} data-testid={`paint-shop-card-${shop.owner_id}`}>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <PaintBucket className="h-4 w-4 text-kilted-green" /> {shop.shop_name}
              <span className="text-xs font-normal text-muted-foreground">{shop.tally.count} orders · ${shop.tally.total.toFixed(2)}</span>
            </CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={!shop.tally.count} onClick={() => downloadStatement(shop.owner_id, data.month, "csv", shop.shop_name)} data-testid={`paint-shop-csv-${shop.owner_id}`} className="gap-1"><FileDown className="h-3.5 w-3.5" /> CSV</Button>
              <Button variant="outline" size="sm" disabled={!shop.tally.count} onClick={() => downloadStatement(shop.owner_id, data.month, "pdf", shop.shop_name)} data-testid={`paint-shop-pdf-${shop.owner_id}`} className="gap-1"><FileText className="h-3.5 w-3.5" /> PDF</Button>
            </div>
          </CardHeader>
          <CardContent>
            {shop.orders.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">No orders this month.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead className="text-right">Disc</TableHead>
                      <TableHead className="text-right">GST</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {shop.orders.map((o) => (
                      <TableRow key={o.id} data-testid={`paint-order-row-${o.id}`}>
                        <TableCell className="whitespace-nowrap text-xs">{o.created_at.slice(0, 10)}</TableCell>
                        <TableCell>
                          <div className="text-sm font-medium">{o.customer.name}</div>
                          {o.customer.phone && <div className="text-xs text-muted-foreground">{o.customer.phone}</div>}
                        </TableCell>
                        <TableCell className="max-w-[260px] text-xs">{o.lines.map((l) => `${l.qty}× ${l.label}`).join(", ")}{o.note && <div className="mt-0.5 italic text-kilted-green" data-testid={`paint-admin-note-${o.id}`}>Note: {o.note}</div>}<div className="mt-1">{o.cancelled_at ? <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">Cancelled by shop {o.cancelled_at.slice(0, 10)}</span> : <PaintDeliveryStatus order={o} />}</div></TableCell>
                        <TableCell className="text-right text-xs">{o.discount ? `-$${o.discount.toFixed(2)}` : "—"}</TableCell>
                        <TableCell className="text-right text-xs">${o.gst.toFixed(2)}</TableCell>
                        <TableCell className="text-right text-sm font-semibold">${o.total.toFixed(2)}</TableCell>
                        <TableCell>
                          {o.billed
                            ? <Badge className="bg-kilted-green text-white" data-testid={`paint-billed-${o.id}`}>Invoiced {o.xero_invoice_number || ""}</Badge>
                            : <Badge variant="outline" className="border-kilted-gold text-kilted-green">Unbilled</Badge>}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" disabled={!!o.billed} onClick={() => setEditing(o)} data-testid={`paint-edit-${o.id}`} className="h-8 w-8 p-0"><Pencil className="h-3.5 w-3.5" /></Button>
                            <Button variant="ghost" size="sm" disabled={!!o.billed} onClick={() => remove(o.id)} data-testid={`paint-delete-${o.id}`} className="h-8 w-8 p-0 text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      {editing && <EditDialog order={editing} items={items} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function EditDialog({ order, items, onClose, onSaved }) {
  const [qty, setQty] = useState(() => Object.fromEntries(items.map((it) => {
    const ln = order.lines.find((l) => l.key === it.key);
    return [it.key, ln ? ln.qty : 0];
  })));
  const [name, setName] = useState(order.customer.name || "");
  const [address, setAddress] = useState(order.customer.address || "");
  const [phone, setPhone] = useState(order.customer.phone || "");
  const [note, setNote] = useState(order.note || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/paint/admin/orders/${order.id}`, {
        items: Object.fromEntries(Object.entries(qty).map(([k, v]) => [k, Number(v) || 0])),
        customer_name: name, customer_address: address, customer_phone: phone, note,
      });
      toast.success("Paint order updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e, "Save failed"));
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="paint-edit-dialog">
        <DialogHeader><DialogTitle>Edit paint order</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {items.map((it) => (
              <div key={it.key}>
                <Label className="text-xs">{it.label} (${it.price.toFixed(2)})</Label>
                <Input type="number" min="0" value={qty[it.key]} onChange={(e) => setQty((q) => ({ ...q, [it.key]: e.target.value }))} data-testid={`paint-edit-qty-${it.key}`} className="mt-1 h-9" />
              </div>
            ))}
          </div>
          <div>
            <Label className="text-xs">Customer name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="paint-edit-name" className="mt-1 h-9" />
          </div>
          <div>
            <Label className="text-xs">Address</Label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} data-testid="paint-edit-address" className="mt-1 h-9" />
          </div>
          <div>
            <Label className="text-xs">Phone</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="paint-edit-phone" className="mt-1 h-9" />
          </div>
          <div>
            <Label className="text-xs">Delivery note (driver run sheet)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value.slice(0, 300))} data-testid="paint-edit-note" className="mt-1 h-9" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="paint-edit-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="paint-edit-save" className="bg-kilted-green text-white hover:bg-kilted-green/90">
            {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}Save & recalculate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
